import axios from 'axios';
import { format } from 'date-fns';

const MFC_HOST = 'myfigurecollection.net';
const PROFILE_SEGMENT = '/profile/';
const REFERENCE_PROFILE_URL = 'https://myfigurecollection.net/profile/makuroko';
const FIRECRAWL_SCRAPE_ENDPOINT = 'https://api.firecrawl.dev/v2/scrape';
const FIRECRAWL_JOBS_ENDPOINT = 'https://api.firecrawl.dev/v2/jobs';
const OWNED_COLLECTION_URL = 'https://myfigurecollection.net/';
const DEFAULT_AVATAR = 'https://myfigurecollection.net/images/global/default/avatar.png';
const STATUS_LABELS = {
  owned: 'Owned',
  ordered: 'Ordered',
  wished: 'Wished'
};
const DEFAULT_FIRECRAWL_TIMEOUT = 45000;
const DEFAULT_FIRECRAWL_POLL_INTERVAL = 5000;

function resolveFirecrawlTimeout() {
  const fromEnv = parseInt(process.env.REACT_APP_FIRECRAWL_TIMEOUT_MS || process.env.FIRECRAWL_TIMEOUT_MS || '', 10);
  if (!Number.isNaN(fromEnv) && fromEnv > 0) {
    return fromEnv;
  }
  return DEFAULT_FIRECRAWL_TIMEOUT;
}

function resolveFirecrawlPollInterval() {
  const fromEnv = parseInt(process.env.REACT_APP_FIRECRAWL_POLL_INTERVAL_MS || process.env.FIRECRAWL_POLL_INTERVAL_MS || '', 10);
  if (!Number.isNaN(fromEnv) && fromEnv > 0) {
    return fromEnv;
  }
  return DEFAULT_FIRECRAWL_POLL_INTERVAL;
}

function ensureProfileUrl(rawUrl) {
  if (!rawUrl) {
    throw new Error('A MyFigureCollection profile link is required.');
  }

  let parsed;
  try {
    parsed = new URL(rawUrl);
  } catch (error) {
    throw new Error('Enter a valid profile URL, e.g. https://myfigurecollection.net/profile/username');
  }

  if (!parsed.hostname.endsWith(MFC_HOST)) {
    throw new Error('This tool only works with myfigurecollection.net profile URLs.');
  }

  if (!parsed.pathname.startsWith(PROFILE_SEGMENT)) {
    throw new Error('Paste the URL from a public profile page (…/profile/username).');
  }

  const username = parsed.pathname.replace(PROFILE_SEGMENT, '').split('/')[0];
  if (!username) {
    throw new Error('Could not find the username in that URL.');
  }

  return {
    username,
    normalizedUrl: `https://${parsed.hostname}${PROFILE_SEGMENT}${username}`
  };
}

function resolveFirecrawlApiKey() {
  const value = (process.env.REACT_APP_FIRECRAWL_API_KEY || process.env.FIRECRAWL_API_KEY || '').trim();
  if (!value) {
    throw new Error('Missing Firecrawl API key. Set REACT_APP_FIRECRAWL_API_KEY in your environment.');
  }
  return value;
}

function extractFirecrawlContent(payload, preferredField) {
  if (!payload) return '';

  const containers = Array.isArray(payload) ? payload : [payload];
  const fallbackField = preferredField === 'raw' ? 'rawHtml' : preferredField;
  const fieldOrder = [
    fallbackField,
    'rawHtml',
    'html',
    'raw',
    'text',
    'markdown',
    'content'
  ];

  for (const container of containers) {
    if (!container) continue;

    if (typeof container === 'string' && container.trim()) {
      return container;
    }

    for (const field of fieldOrder) {
      if (!field) continue;
      const value = container[field];
      if (typeof value === 'string' && value.trim()) {
        return value;
      }
    }

    if (container.result && typeof container.result === 'string' && container.result.trim()) {
      return container.result;
    }

    if (container.data && typeof container.data === 'string' && container.data.trim()) {
      return container.data;
    }
  }

  return '';
}

function normalizeFormats(preferredField) {
  // v2 format: use string formats for basics, object formats for JSON/screenshots
  const formatMap = {
    'raw': 'rawHtml',
    'text': 'markdown',
    'html': 'html',
    'rawHtml': 'rawHtml',
    'markdown': 'markdown'
  };
  
  const formatOrder = ['html', 'rawHtml', 'markdown'];
  const requestedFormats = Array.from(new Set([preferredField, ...formatOrder]));
  
  return requestedFormats
    .map((format) => {
      const mapped = formatMap[format];
      if (mapped) {
        return mapped; // v2 uses string formats for basics
      }
      return null;
    })
    .filter(Boolean);
}

function sleep(ms) {
  return new Promise((resolve) => {
    if (ms <= 0) {
      resolve();
      return;
    }
    setTimeout(resolve, ms);
  });
}

async function fetchWithFirecrawl(url, { preferredField = 'html' } = {}) {
  const apiKey = resolveFirecrawlApiKey();
  const formats = normalizeFormats(preferredField);
  if (!formats.length) {
    formats.push('html'); // v2 uses string formats
  }

  const timeoutMs = resolveFirecrawlTimeout();
  const pollInterval = resolveFirecrawlPollInterval();

  console.log('[Firecrawl] Starting scrape request:', { url, formats, timeoutMs, pollInterval });

  try {
    // v2: Use /v2/scrape endpoint directly (not /v2/jobs)
    console.log('[Firecrawl] Calling /v2/scrape endpoint...');
    const scrapeResponse = await axios.post(
      FIRECRAWL_SCRAPE_ENDPOINT,
      {
        url,
        formats,
        // v2 has sensible defaults: blockAds, skipTlsVerification, removeBase64Images are enabled
        // mobile: false is no longer needed as default behavior is better
      },
      {
        headers: {
          Authorization: `Bearer ${apiKey}`,
          'Content-Type': 'application/json'
        },
        timeout: timeoutMs
      }
    );

    const scrapeData = scrapeResponse.data ?? {};
    const fallbackField = preferredField === 'raw' ? 'rawHtml' : preferredField;

    console.log('[Firecrawl] Scrape response received:', {
      hasData: !!scrapeData.data,
      hasJobId: !!(scrapeData.jobId || scrapeData.data?.jobId || scrapeData.id),
      jobId: scrapeData.jobId || scrapeData.data?.jobId || scrapeData.id
    });

    // Check if we got immediate data (success response)
    if (scrapeData.data && !scrapeData.jobId) {
      console.log('[Firecrawl] Received immediate data, extracting content...');
      const content = extractFirecrawlContent(scrapeData.data, fallbackField);
      if (content) {
        console.log('[Firecrawl] Content extracted successfully, returning immediately');
        return content;
      }
      console.log('[Firecrawl] No content found in immediate data, checking for async job...');
    }

    // If async job, extract jobId and poll for status
    const jobId = scrapeData.jobId || scrapeData.data?.jobId || scrapeData.id;
    if (!jobId) {
      // If no jobId and no data, try to extract from response structure
      if (scrapeData.success === false || scrapeData.error) {
        const reason = scrapeData.error?.message || scrapeData.message || 'Firecrawl request failed.';
        console.error('[Firecrawl] Request failed:', reason);
        throw new Error(reason);
      }
      console.error('[Firecrawl] No jobId or data returned from scrape endpoint');
      throw new Error('Firecrawl did not return data or a job identifier.');
    }

    console.log('[Firecrawl] Async job started, jobId:', jobId);
    console.log('[Firecrawl] Starting status polling (interval:', pollInterval, 'ms, timeout:', timeoutMs, 'ms)');

    // Poll for job status using /v2/jobs/{jobId}
    // Check immediately first, then poll periodically
    const jobDeadline = Date.now() + timeoutMs;
    let isFirstCheck = true;
    let checkCount = 0;

    while (Date.now() < jobDeadline) {
      // Sleep before checking, but skip sleep on first iteration for immediate check
      if (!isFirstCheck) {
        console.log('[Firecrawl] Waiting', pollInterval, 'ms before next status check...');
        await sleep(pollInterval);
      }
      isFirstCheck = false;
      checkCount++;

      const timeRemaining = Math.round((jobDeadline - Date.now()) / 1000);
      console.log(`[Firecrawl] Checking job status (check #${checkCount}, ${timeRemaining}s remaining)...`);

      const statusResponse = await axios.get(
        `${FIRECRAWL_JOBS_ENDPOINT}/${jobId}`,
        {
          headers: {
            Authorization: `Bearer ${apiKey}`
          },
          timeout: timeoutMs
        }
      );

      const statusData = statusResponse.data ?? {};
      const status = statusData.status || statusData.state || statusData.data?.status;

      console.log('[Firecrawl] Status check result:', {
        status,
        hasData: !!statusData.data,
        hasResult: !!statusData.result
      });

      // Check for completed states
      if (['completed', 'finished', 'success', 'done'].includes(status?.toLowerCase())) {
        console.log('[Firecrawl] Job completed successfully, extracting content...');
        const payload = statusData.data ?? statusData.result ?? statusData;
        const content = extractFirecrawlContent(payload, fallbackField);
        if (!content) {
          console.error('[Firecrawl] Job completed but no content found in response');
          throw new Error('Firecrawl completed without returning content.');
        }
        console.log('[Firecrawl] Content extracted successfully, returning result');
        return content;
      }

      // Check for failed states
      if (['failed', 'error', 'cancelled'].includes(status?.toLowerCase())) {
        const reason = statusData.error?.message || statusData.message || 'Firecrawl job failed.';
        console.error('[Firecrawl] Job failed:', { status, reason });
        throw new Error(reason);
      }

      // Continue polling if status is 'processing', 'pending', 'in_progress', etc.
      // The loop will continue and check again after pollInterval
      console.log('[Firecrawl] Job still in progress, status:', status);
    }

    console.error('[Firecrawl] Job timed out after', checkCount, 'status checks');
    throw new Error('Firecrawl job timed out. Try again or increase REACT_APP_FIRECRAWL_TIMEOUT_MS.');
  } catch (error) {
    if (error?.code === 'ECONNABORTED') {
      console.error('[Firecrawl] Request connection timeout');
      throw new Error('Firecrawl request timed out. Try again or increase REACT_APP_FIRECRAWL_TIMEOUT_MS.');
    }
    // Handle axios response errors
    if (error?.response?.data) {
      const errorData = error.response.data;
      const reason = errorData.error?.message || errorData.message || 'Firecrawl API error.';
      console.error('[Firecrawl] API error:', { status: error.response.status, reason });
      throw new Error(reason);
    }
    console.error('[Firecrawl] Job failed for', url, error);
    throw error instanceof Error ? error : new Error('Failed to fetch content from MyFigureCollection through Firecrawl.');
  }
}

function parseHtml(html) {
  return new DOMParser().parseFromString(html, 'text/html');
}

function safeTextContent(element) {
  return element?.textContent ? element.textContent.trim() : '';
}

function extractProfileMeta(doc, property, attribute = 'content') {
  const node = doc.querySelector(`meta[${property}]`);
  return node ? node.getAttribute(attribute) : '';
}

function extractProfileInfo(profileHtmlDoc, username, normalizedUrl) {
  const fallbackName = username;
  const displayName = extractProfileMeta(profileHtmlDoc, 'property="og:title"')
    .replace(/\| MyFigureCollection\.net.*/i, '')
    .replace(/Profile/i, '')
    .trim() || fallbackName;

  const avatarUrl = extractProfileMeta(profileHtmlDoc, 'property="og:image"') || DEFAULT_AVATAR;

  const possibleLocationSelectors = [
    '.profile-infos__item--location .profile-infos__value',
    '.profile-location',
    '[itemprop="homeLocation"]',
    '.profile__location'
  ];

  let location = '';
  for (const selector of possibleLocationSelectors) {
    const candidate = safeTextContent(profileHtmlDoc.querySelector(selector));
    if (candidate) {
      location = candidate;
      break;
    }
  }

  if (!location) {
    const geoMeta = extractProfileMeta(profileHtmlDoc, 'name="geo:placename"');
    if (geoMeta) {
      location = geoMeta;
    }
  }

  const possibleLastOnlineSelectors = [
    '.profile-infos__item--lastonline time',
    'time[itemprop="lastActivity"]'
  ];

  let lastOnline = '';
  for (const selector of possibleLastOnlineSelectors) {
    const timeNode = profileHtmlDoc.querySelector(selector);
    if (timeNode) {
      lastOnline = timeNode.getAttribute('datetime') || safeTextContent(timeNode);
      if (lastOnline) break;
    }
  }

  return {
    username,
    displayName: displayName || fallbackName,
    url: normalizedUrl,
    avatarUrl,
    location: location || '—',
    lastOnline: lastOnline || null
  };
}

function parseExternalId(link) {
  if (!link) return '';
  const match = link.match(/(?:item|entry|product)\/(\d+)/i);
  return match ? match[1] : link;
}

function parseScale(fields) {
  const scale = fields.scale || fields['scale ratio'] || '';
  if (!scale) return '';
  if (/^1\//.test(scale)) {
    return scale;
  }
  if (/non/i.test(scale)) {
    return 'Non-scale';
  }
  return scale;
}

function parseProductCode(fields) {
  return fields['product code'] || fields['item code'] || fields['jan'] || '';
}

function parseRelease(fields) {
  return fields.release || fields.released || fields['release date'] || '';
}

function parseAltMetadata(altText = '') {
  const trimmed = altText.trim();
  if (!trimmed) {
    return {};
  }

  const brandMatch = trimmed.match(/\(([^()]+)\)\s*$/);
  const brand = brandMatch ? brandMatch[1].trim() : '';
  const withoutBrand = brandMatch ? trimmed.slice(0, brandMatch.index).trim() : trimmed;

  const parts = withoutBrand.split(' - ').map((part) => part.trim()).filter(Boolean);

  let series = '';
  let name = '';
  let scale = '';
  let remainder = '';

  if (parts.length > 0) {
    series = parts[0];
  }
  if (parts.length > 1) {
    name = parts[1];
  }
  if (parts.length > 2) {
    scale = parts[2];
  }
  if (parts.length > 3) {
    remainder = parts.slice(3).join(' - ');
  }

  return {
    brand,
    series,
    name,
    scale,
    type: remainder
  };
}

async function fetchCollectionFeed(username, statusKey) {
  if (statusKey !== 'owned') {
    return [];
  }

  const params = new URLSearchParams({
    _tb: 'user',
    mode: 'view',
    username,
    tab: 'collection',
    rootId: '0',
    status: '2'
  });
  const url = `${OWNED_COLLECTION_URL}?${params.toString()}`;
  try {
    const html = await fetchWithFirecrawl(url, { preferredField: 'html' });
    const doc = parseHtml(html);

    let cardNodes = Array.from(doc.querySelectorAll('.collection-item, .collection__item'));

    if (cardNodes.length === 0) {
      cardNodes = Array.from(doc.querySelectorAll('.item-icon'));
    }

    const items = cardNodes.map((card) => {
      const linkNode = card.querySelector('a[href*="/item/"]') || card.querySelector('a[href*="/entry/"]');
      const link = linkNode ? linkNode.getAttribute('href') : '';
      const externalId = parseExternalId(link);

      const imageNode = card.querySelector('img');
      const imageUrl = imageNode ? imageNode.getAttribute('data-src') || imageNode.getAttribute('src') : '';
      const altMetadata = parseAltMetadata(imageNode?.getAttribute('alt'));

      const name = (linkNode ? linkNode.textContent.trim() : safeTextContent(card.querySelector('.collection-item__name'))) || altMetadata.name || altMetadata.series || '';

      const metaBlocks = Array.from(card.querySelectorAll('.collection-item__meta, .collection-item__info, .collection__item__info'));
      const fields = {};

      metaBlocks.forEach((block) => {
        block.querySelectorAll('li, p, span').forEach((node) => {
          const text = node.textContent.trim();
          if (!text || text.includes('Owned since')) return;
          const [rawLabel, ...rest] = text.split(':');
          if (!rawLabel || rest.length === 0) return;
          const label = rawLabel.toLowerCase().trim();
          const value = rest.join(':').trim();
          if (label && value) {
            fields[label] = value;
          }
        });
      });

      const brand = fields.manufacturer || fields.company || fields['originating company'] || fields.brand || altMetadata.brand || '';
      const series = fields.origin || fields.series || fields['source material'] || altMetadata.series || '';
      const type = fields.category || fields.classification || fields.type || altMetadata.type || '';
      const scale = parseScale(fields) || altMetadata.scale || '—';
      const productCode = parseProductCode(fields);
      const releaseDate = parseRelease(fields) || fields.release || '';

      return {
        externalId,
        name: name || 'Untitled Item',
        brand,
        series,
        type: type || 'Figure',
        scale,
        productCode,
        status: STATUS_LABELS.owned,
        releaseDate,
        imageUrl: imageUrl || ''
      };
    });

    return items.filter((item) => item.name);
  } catch (error) {
    console.warn(`Unable to load ${statusKey} feed for ${username}:`, error);
    return [];
  }
}

function buildSegments(items) {
  const counts = new Map();
  items.forEach((item) => {
    const key = item.type || 'Uncategorized';
    counts.set(key, (counts.get(key) || 0) + 1);
  });

  return Array.from(counts.entries())
    .map(([key, count]) => ({ key, label: key, count }))
    .sort((a, b) => b.count - a.count)
    .slice(0, 6);
}

function truncateItems(items, limit = 5) {
  return items.slice(0, limit).map(({ fields, ...rest }) => rest);
}

export const myFigureCollectionService = {
  referenceProfileUrl: REFERENCE_PROFILE_URL,

  normalizeProfileUrl(rawUrl) {
    return ensureProfileUrl(rawUrl).normalizedUrl;
  },

  async previewImport(rawUrl) {
    const profile = ensureProfileUrl(rawUrl);

    try {
      const [profileHtml, ownedItems] = await Promise.all([
        fetchWithFirecrawl(profile.normalizedUrl, { preferredField: 'html' }),
        fetchCollectionFeed(profile.username, 'owned'),
        // Note: ordered and wished feeds are fetched but results not used yet
        fetchCollectionFeed(profile.username, 'ordered').catch(() => []),
        fetchCollectionFeed(profile.username, 'wished').catch(() => [])
      ]);

      const profileDoc = parseHtml(profileHtml);
      const profileInfo = extractProfileInfo(profileDoc, profile.username, profile.normalizedUrl);

      const summary = {
        owned: ownedItems.length,
        ordered: 0,
        wished: 0,
        listedForSale: 0,
        lastSyncedLabel: format(new Date(), 'PPP p')
      };

      const combinedItems = ownedItems;
      const segments = buildSegments(combinedItems);
      const sampleItems = truncateItems(combinedItems);

      return {
        requestedUrl: profile.normalizedUrl,
        username: profile.username,
        profile: profileInfo,
        summary,
        segments,
        sampleItems
      };
    } catch (error) {
      console.error('Failed to load MyFigureCollection preview:', error);
      throw new Error(error.message || 'We could not fetch data from MyFigureCollection. Make sure the profile is public and try again.');
    }
  }
};
